/**
 * Save for Later Custom API Implementation
 * Handles Save for Later functionality with custom properties (cprops)
 * Based on Swym API: https://developers.getswym.com/docs/add-product-to-save-for-later
 */

function sflRollbar(context, err, extra) {
  if (!window.Rollbar) return;
  try {
    const message =
      err && typeof err === 'object' && err.message
        ? String(err.message)
        : typeof err === 'string'
          ? err
          : 'SaveForLater error';
    Rollbar.error(message, err instanceof Error ? err : { detail: err }, {
      context: 'SaveForLater.' + context,
      ...(extra && typeof extra === 'object' ? extra : {}),
    });
  } catch (_) {}
}

function wrapSFLPrototype(Ctor, className) {
  if (!Ctor || !Ctor.prototype) return;
  const proto = Ctor.prototype;
  Object.getOwnPropertyNames(proto).forEach((name) => {
    if (name === 'constructor') return;
    const desc = Object.getOwnPropertyDescriptor(proto, name);
    if (!desc || typeof desc.value !== 'function') return;
    const original = desc.value;
    proto[name] = function sflWrapped(...args) {
      try {
        const result = original.apply(this, args);
        if (result != null && typeof result.then === 'function') {
          return result.catch((e) => {
            sflRollbar(`${className}.${name}`, e);
            return Promise.reject(e);
          });
        }
        return result;
      } catch (e) {
        sflRollbar(`${className}.${name}`, e);
        throw e;
      }
    };
  });
}

class SaveForLaterManager {
  constructor() {
    try {
      this.listId = null;
      this.isInitialized = false;
      this.initCallbacks = [];
      this.init();
    } catch (e) {
      sflRollbar('SaveForLaterManager.constructor', e);
    }
  }

  /**
   * Initialize Save for Later
   * Waits for Swym to be ready and initializes the SFL list
   */
  init() {
    if (typeof window.SwymCallbacks === 'undefined') {
      window.SwymCallbacks = [];
    }

    window.SwymCallbacks.push((swat) => {
      if (!swat || !swat.SaveForLater || typeof swat.SaveForLater.init !== 'function') {
        console.warn('[SFL] Swym SaveForLater is not available');
        return;
      }

      swat.SaveForLater.init(
        (response) => {
          const lid = response && response.list && response.list.lid;
          if (!lid) {
            sflRollbar('init.SwymCallback.noListId', new Error('Swym SaveForLater.init returned no list id'));
            return;
          }

          this.listId = lid;
          this.isInitialized = true;
          window._saveforLaterListId = this.listId;

          // Execute any pending callbacks
          const pending = this.initCallbacks;
          this.initCallbacks = [];
          pending.forEach((cb) => {
            try {
              cb(this.listId);
            } catch (e) {
              sflRollbar('init.SwymCallback.pendingCallback', e);
            }
          });

          console.log('[SFL] Initialized with list ID:', this.listId);
        },
        (error) => {
          console.error('[SFL] Error initializing Save for Later:', error);

          let detail = error;
          try {
            detail = typeof error === 'string' ? error : (error && error.message) || JSON.stringify(error);
          } catch (_) {}

          // "Server API Error - 0" means Swym got no HTTP response (offline, blocked by an
          // ad-blocker/extension, or a bot/crawler). It is outside the theme's control, so don't report it.
          if (/Server API Error\s*-\s*0\b/i.test(String(detail))) return;

          sflRollbar('init.SwymCallback', error instanceof Error ? error : new Error('Swym SaveForLater.init failed: ' + detail), { swymError: detail });
        }
      );
    });

    this.setupSwymMoveToCartInterceptor();
  }

  /**
   * Intercept clicks on Swym's Move to Cart button (class swym-storefront-layout-grid-item-add-to-cart-button)
   * so we add to cart with line item properties (cprops) from the SFL list.
   */
  setupSwymMoveToCartInterceptor() {
    if (this._moveToCartBound) return;
    this._moveToCartBound = true;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.swym-storefront-layout-grid-item-add-to-cart-button');
      if (!btn || !window.saveForLaterManager) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const manager = window.saveForLaterManager;

      (async () => {
        btn.setAttribute('disabled', 'disabled');
        btn.classList.add('loading');

        try {
          let sflItem = await manager.resolveSFLItemFromButton(btn);

          if (!sflItem || (sflItem.epi == null && sflItem.variant_id == null)) {
            sflRollbar('setupSwymMoveToCartInterceptor.resolve', new Error('Could not resolve SFL item for Move to Cart'));
            console.error('[SFL] Could not resolve SFL item for Move to Cart');
            return;
          }

          manager.moveToCart(
            sflItem,
            (response) => {
              manager.updateSwymSFLBadge();
              manager.refreshCartUI();
              if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
                window.notificationEle.updateNotification('Added to Cart', 'Item has been moved to your cart', { type: 'success', timeout: 3000 });
              }
            },
            (error) => {
              if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
                window.notificationEle.updateNotification('Error', 'Failed to move item to cart. Please try again.', { type: 'error', timeout: 5000 });
              }
            }
          );
        } catch (e) {
          sflRollbar('setupSwymMoveToCartInterceptor', e);
        } finally {
          btn.removeAttribute('disabled');
          btn.classList.remove('loading');
        }
      })();
    }, true);
  }

  /**
   * Resolve the SFL list item for a Move to Cart button (index, DOM, or URL match).
   * @param {Element} button - Move to Cart button element
   * @returns {Promise<Object|null>} Item with epi, empi, du, qty, cprops or null
   */
  async resolveSFLItemFromButton(button) {
    const listContents = await this.getSFLListContents();
    const list = Array.isArray(listContents) ? listContents : (listContents?.data || listContents?.items || listContents?.list || []);

    if (!Array.isArray(list) || list.length === 0) {
      return this.getSFLItemFromGridItem(button);
    }

    const sflTab = document.querySelector('[data-view="tabSavedForLater"]');
    const container = sflTab || document.getElementById('swym-storefront-layout-container') || button.closest('.swym-storefront-layout') || document.body;
    const buttons = container.querySelectorAll('.swym-storefront-layout-grid-item-add-to-cart-button');
    const index = Array.prototype.indexOf.call(buttons, button);

    if (index >= 0 && list[index]) {
      return list[index];
    }

    const fromDom = this.getSFLItemFromGridItem(button);
    if (fromDom && (fromDom.epi != null || fromDom.du)) {
      if (fromDom.epi != null) {
        const byEpi = list.find((i) => String(i.epi) === String(fromDom.epi));
        if (byEpi) return byEpi;
      }
      if (fromDom.du) {
        const norm = (u) => (u || '').replace(/^https?:\/\//, '').split('?')[0].toLowerCase();
        const byDu = list.find((i) => norm(i.du) === norm(fromDom.du) || (i.du && fromDom.du && i.du.includes(fromDom.du)));
        if (byDu) return byDu;
      }
      return fromDom;
    }

    if (list.length === 1) return list[0];
    return null;
  }

  /**
   * Try to get SFL item data from the grid item DOM (data attributes or product link).
   * Uses parent to find the card; the button itself must not be treated as the grid item.
   * @param {Element} button - Move to Cart button element
   * @returns {Object|null} Item with epi, empi, du, qty, cprops or null
   */
  getSFLItemFromGridItem(button) {
    const gridItem = button.closest('.swym-storefront-layout-grid-item') ||
      (button.parentElement && button.parentElement.closest('[class*="swym-storefront-layout-grid-item"]'));
    if (!gridItem || gridItem === button) return null;

    let du = gridItem.dataset.du || gridItem.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('?')[0] || '';
    if (du && du.startsWith('/')) du = window.location.origin + du;
    const epi = gridItem.dataset.epi != null ? gridItem.dataset.epi : (() => {
      const link = gridItem.querySelector('a[href*="/products/"]');
      if (!link) return null;
      const href = link.getAttribute('href') || '';
      const match = href.match(/[?&]variant=(\d+)/);
      return match ? match[1] : null;
    })();
    const empi = gridItem.dataset.empi || null;
    const qty = gridItem.dataset.qty != null ? gridItem.dataset.qty : 1;
    let cprops = {};
    try {
      if (gridItem.dataset.cprops) cprops = JSON.parse(gridItem.dataset.cprops);
    } catch (e) {
      sflRollbar('getSFLItemFromGridItem.cpropsParse', e);
    }

    if (!du && epi == null) return null;
    return { du: du || undefined, empi: empi ? parseInt(empi, 10) : undefined, epi: epi != null ? parseInt(epi, 10) : undefined, qty: parseInt(qty, 10) || 1, cprops };
  }

  /**
   * Wait for initialization to complete
   * @returns {Promise<string>} Resolves with list ID when ready
   */
  waitForInit() {
    return new Promise((resolve) => {
      if (this.isInitialized && this.listId) {
        resolve(this.listId);
      } else {
        this.initCallbacks.push(resolve);
      }
    });
  }

  /**
   * Build custom properties (cprops) object from cart item properties
   * @param {Object} itemProperties - Properties from cart item
   * @returns {Object} cprops object for Swym API
   */
  buildCprops(itemProperties) {
    if (!itemProperties || typeof itemProperties !== 'object') {
      return {};
    }

    const cprops = {};
    
    // Copy all properties that don't start with underscore (internal properties)
    Object.keys(itemProperties).forEach(key => {
      if (!key.startsWith('_') && itemProperties[key] !== null && itemProperties[key] !== '') {
        cprops[key] = itemProperties[key];
      }
    });

    return cprops;
  }

  /**
   * Add product to Save for Later list with custom properties
   * @param {Object} productData - Product data including properties
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  async addToSFL(productData, onSuccess, onError) {
    try {
      const listId = await this.waitForInit();

      if (!productData.du || !productData.empi || !productData.epi) {
        throw new Error('Missing required product data (du, empi, epi)');
      }

      // Build the product object with cprops
      const product = [{
        du: productData.du,
        empi: parseInt(productData.empi),
        epi: parseInt(productData.epi),
        qty: parseInt(productData.qty) || 1,
        cprops: this.buildCprops(productData.properties)
      }];

      console.log('productData', productData, product);

      // Add note if provided
      if (productData.note) {
        product[0].note = productData.note;
      }

      console.log('[SFL] Adding product with cprops:', product);

      _swat.SaveForLater.add(
        listId,
        product,
        (response) => {
          console.log('[SFL] Successfully added to Save for Later:', response);
          this.updateSwymSFLBadge();
          if (typeof onSuccess === 'function') {
            onSuccess(response);
          }
        },
        (error) => {
          console.error('[SFL] Error adding to Save for Later:', error);
          if (typeof onError === 'function') {
            onError(error);
          }
        }
      );
    } catch (error) {
      sflRollbar('addToSFL', error);
      console.error('[SFL] Error in addToSFL:', error);
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  }

  /**
   * Remove product from Save for Later list
   * @param {Object} productData - Product data (du, empi, epi)
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  async removeFromSFL(productData, onSuccess, onError) {
    try {
      const listId = await this.waitForInit();

      const product = [{
        du: productData.du,
        empi: parseInt(productData.empi),
        epi: parseInt(productData.epi)
      }];

      console.log('[SFL] Removing product:', product);

      _swat.SaveForLater.remove(
        listId,
        product,
        (response) => {
          console.log('[SFL] Successfully removed from Save for Later:', response);
          this.updateSwymSFLBadge();
          if (typeof onSuccess === 'function') {
            onSuccess(response);
          }
        },
        (error) => {
          console.error('[SFL] Error removing from Save for Later:', error);
          if (typeof onError === 'function') {
            onError(error);
          }
        }
      );
    } catch (error) {
      sflRollbar('removeFromSFL', error);
      console.error('[SFL] Error in removeFromSFL:', error);
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  }

  /**
   * Fetch Save for Later list contents
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  async fetchSFLList(onSuccess, onError) {
    try {
      const listId = await this.waitForInit();

      _swat.fetchListCtx(
        { lid: listId },
        (listContents) => {
          console.log('[SFL] Fetched Save for Later list:', listContents);
          if (typeof onSuccess === 'function') {
            onSuccess(listContents);
          }
        },
        (error) => {
          console.error('[SFL] Error fetching Save for Later list:', error);
          if (typeof onError === 'function') {
            onError(error);
          }
        }
      );
    } catch (error) {
      sflRollbar('fetchSFLList', error);
      console.error('[SFL] Error in fetchSFLList:', error);
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  }

  /**
   * Get SFL list contents as a Promise (for use in moveToCart)
   * @returns {Promise<Array>} List items with epi, empi, du, qty, cprops, etc.
   */
  getSFLListContents() {
    return new Promise((resolve, reject) => {
      this.fetchSFLList(resolve, reject);
    });
  }

  /**
   * Normalize item from Swym drawer (may use different property names)
   * @param {Object} item - Raw item from Swym moveToCart
   * @returns {Object} Normalized { du, empi, epi, qty, cprops }
   */
  normalizeSFLItem(item) {
    if (!item || typeof item !== 'object') return null;
    const epi = item.epi != null ? item.epi : item.variant_id;
    const empi = item.empi != null ? item.empi : item.product_id;
    const du = item.du || item.url || item.product_url;
    const qty = item.qty != null ? item.qty : (item.quantity != null ? item.quantity : 1);
    const cprops = item.cprops && typeof item.cprops === 'object' ? item.cprops : {};
    return {
      du: du,
      empi: empi,
      epi: epi,
      qty: parseInt(qty, 10) || 1,
      cprops: cprops
    };
  }

  /**
   * Normalize cprops (from Swym or DOM) to Shopify cart line item properties format:
   * plain object { "Property Name": "value", ... }.
   * Handles: array of [key, value] pairs, object with numeric keys and [key, value] values, or already plain object.
   * @param {Object|Array} cprops - Raw cprops from list or DOM
   * @returns {Object} { "Ship Date": "Feb 24, 2026", "Label Description": "...", ... }
   */
  normalizeCpropsToCartProperties(cprops) {
    const out = {};
    if (cprops == null || typeof cprops !== 'object') return out;

    if (Array.isArray(cprops)) {
      cprops.forEach((entry) => {
        if (Array.isArray(entry) && entry.length >= 2 && String(entry[0]).charAt(0) !== '_') {
          const val = entry[1];
          if (val !== null && val !== '') out[String(entry[0])] = String(val);
        } else if (entry && typeof entry === 'object' && (entry.name != null || entry.key != null)) {
          const k = entry.name != null ? entry.name : entry.key;
          const v = entry.value != null ? entry.value : entry.val;
          if (k && String(k).charAt(0) !== '_' && v !== null && v !== '') out[String(k)] = String(v);
        }
      });
      return out;
    }

    Object.keys(cprops).forEach((key) => {
      if (key.startsWith('_')) return;
      const v = cprops[key];
      if (v === null || v === '') return;
      if (Array.isArray(v) && v.length >= 2) {
        out[String(v[0])] = String(v[1]);
      } else if (typeof v === 'string' && (v.startsWith('[') || v.startsWith('{'))) {
        try {
          const parsed = JSON.parse(v);
          const normalized = this.normalizeCpropsToCartProperties(parsed);
          Object.assign(out, normalized);
        } catch (_) {
          out[String(key)] = v;
        }
      } else if (typeof v === 'object') {
        return;
      } else {
        out[String(key)] = String(v);
      }
    });
    return out;
  }

  /**
   * Move item from Save for Later back to cart with properties
   * @param {Object} sflItem - Item from Save for Later list (from Swym UI or data attrs)
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  async moveToCart(sflItem, onSuccess, onError) {
    try {
      const normalized = this.normalizeSFLItem(sflItem);
      if (!normalized || normalized.epi == null || normalized.empi == null || !normalized.du) {
        throw new Error('Invalid SFL item: missing epi, empi, or du');
      }

      let cprops = normalized.cprops && typeof normalized.cprops === 'object' ? normalized.cprops : {};
      if (Object.keys(cprops).length === 0 && typeof _swat !== 'undefined' && _swat.fetchListCtx) {
        try {
          const listContents = await this.getSFLListContents();
          const listItem = Array.isArray(listContents)
            ? listContents.find((i) => String(i.epi) === String(normalized.epi))
            : null;
          if (listItem && listItem.cprops && typeof listItem.cprops === 'object') {
            cprops = listItem.cprops;
          }
        } catch (e) {
          sflRollbar('moveToCart.getSFLListForCprops', e);
          console.warn('[SFL] Could not fetch SFL list for cprops:', e);
        }
      }

      const properties = this.normalizeCpropsToCartProperties(cprops);

      // Build the cart add payload
      const cartPayload = {
        items: [{
          id: parseInt(normalized.epi, 10),
          quantity: normalized.qty,
          properties: properties
        }]
      };

      console.log('[SFL] Moving to cart with properties:', cartPayload);

      // Add to cart using Shopify Cart API
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartPayload)
      });

      const data = await response.json();

      if (response.ok) {
        console.log('[SFL] Successfully added to cart:', data);

        // Remove from Save for Later after successful cart add
        this.removeFromSFL(
          {
            du: normalized.du,
            empi: normalized.empi,
            epi: normalized.epi
          },
          () => {
            console.log('[SFL] Removed from SFL after moving to cart');
            
            // Trigger cart update event
            if (window.eventBus && window.PUB_SUB_EVENTS) {
              window.eventBus.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                data: {
                  action: 'add-from-sfl',
                  item: data
                }
              });
            }
            
            // Update cart UI
            this.refreshCartUI();
            
            if (typeof onSuccess === 'function') {
              onSuccess(data);
            }
          },
          (error) => {
            // Still call success since item was added to cart
            console.warn('[SFL] Item added to cart but failed to remove from SFL:', error);
            this.refreshCartUI();
            if (typeof onSuccess === 'function') {
              onSuccess(data);
            }
          }
        );
      } else {
        throw new Error(data.description || 'Failed to add item to cart');
      }
    } catch (error) {
      sflRollbar('moveToCart', error);
      console.error('[SFL] Error moving to cart:', error);
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  }

  /**
   * Update Swym Save for Later badge count (e.g. .swym-anchor-badge).
   * Call after add to SFL, remove from SFL, or move to cart so the badge shows the correct total.
   */
  updateSwymSFLBadge() {
    const refreshList = () => {
      if (window.SwymStorefrontLayoutExtensions && typeof window.SwymStorefrontLayoutExtensions.refreshSFLList === 'function') {
        window.SwymStorefrontLayoutExtensions.refreshSFLList();
      }
    };

    const updateBadgeCount = (count) => {
      const badges = document.querySelectorAll('.swym-anchor-badge');
      badges.forEach((el) => {
        const num = parseInt(count, 10) || 0;
        el.textContent = num > 0 ? String(num) : '';
        el.style.display = num > 0 ? '' : 'none';
      });
    };

    refreshList();

    if (typeof _swat !== 'undefined' && _swat.fetchListCtx) {
      this.getSFLListContents()
        .then((list) => {
          const arr = Array.isArray(list) ? list : (list?.data || list?.items || list?.list || []);
          const count = Array.isArray(arr) ? arr.length : 0;
          updateBadgeCount(count);
        })
        .catch(() => updateBadgeCount(0));
    } else {
      updateBadgeCount(0);
    }
  }

  /**
   * Refresh cart UI after modifications
   */
  refreshCartUI() {
    // Try to refresh the cart form component
    const cartForm = document.querySelector('cart-form');
    if (cartForm && typeof cartForm.getCartData === 'function') {
      cartForm.getCartData();
    } else {
      // Fallback: reload the page if on cart page
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
      }
    }

    // Update header cart count
    if (typeof updateCartCountInHeader === 'function') {
      fetch(window.routes?.cart_url || '/cart.js')
        .then(res => res.json())
        .then(cart => {
          window.globalSpace = window.globalSpace || {};
          window.globalSpace.cart = cart;
          updateCartCountInHeader();
        });
    }
  }
}

wrapSFLPrototype(SaveForLaterManager, 'SaveForLaterManager');

/**
 * Custom Element for Save for Later button on cart items
 * Passes product properties as cprops to the SFL API
 */
class SaveForLaterButton extends HTMLElement {
  constructor() {
    super();
    try {
      this.button = this.querySelector('button, .save-for-later-btn');
      this.manager = window.saveForLaterManager;
      if (this.button) {
        this.button.addEventListener('click', this.handleClick.bind(this));
      }
    } catch (e) {
      sflRollbar('SaveForLaterButton.constructor', e);
    }
  }

  connectedCallback() {
    // Ensure manager is available
    if (!window.saveForLaterManager) {
      window.saveForLaterManager = new SaveForLaterManager();
      this.manager = window.saveForLaterManager;
    }
  }

  /**
   * Get product properties from data attributes or cart item
   * @returns {Object} Properties object
   */
  getProductProperties() {
    const properties = {};
    
    // Try to get properties from data attribute
    const propsData = this.button.dataset.properties || this.dataset.properties;
    if (propsData) {
      try {
        const parsed = JSON.parse(propsData);
        Object.assign(properties, parsed);
      } catch (e) {
        sflRollbar('SaveForLaterButton.getProductProperties.parse', e);
        console.warn('[SFL] Could not parse properties data:', e);
      }
    }

    // Also look for individual property data attributes
    const dataset = this.button.dataset;
    Object.keys(dataset).forEach(key => {
      if (key.startsWith('prop')) {
        // Convert propLabelDescription to 'Label Description'
        const propName = key.replace('prop', '').replace(/([A-Z])/g, ' $1').trim();
        properties[propName] = dataset[key];
      }
    });

    return properties;
  }

  /**
   * Handle save for later button click
   */
  async handleClick(event) {
    event.preventDefault();
    
    const button = this.button;
    const productUrl = button.dataset.productUrl;
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const quantity = button.dataset.quantity || 1;
    const properties = this.getProductProperties();

    if (!productUrl || !productId || !variantId) {
      console.error('[SFL] Missing required product data');
      return;
    }

    // Show loading state
    button.classList.add('loading');
    button.setAttribute('disabled', 'disabled');

    const productData = {
      du: productUrl,
      empi: productId,
      epi: variantId,
      qty: quantity,
      properties: properties
    };

    this.manager.addToSFL(
      productData,
      (response) => {
        // Success - remove item from cart
        const cartRemoveButton = this.closest('.cart-item, .cart-items, [data-cart-item]')
          ?.querySelector('[data-item-remove]');
        
        if (cartRemoveButton) {
          cartRemoveButton.dispatchEvent(new CustomEvent('click'));
        }

        // Show success message
        if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
          window.notificationEle.updateNotification(
            'Saved for Later',
            'Item has been saved to your Save for Later list',
            { type: 'success', timeout: 3000 }
          );
        }

        button.classList.remove('loading');
        button.removeAttribute('disabled');
      },
      (error) => {
        // Error handling
        if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
          window.notificationEle.updateNotification(
            'Error',
            'Failed to save item for later. Please try again.',
            { type: 'error', timeout: 5000 }
          );
        }

        button.classList.remove('loading');
        button.removeAttribute('disabled');
      }
    );
  }
}

wrapSFLPrototype(SaveForLaterButton, 'SaveForLaterButton');

/**
 * Custom Element for Move to Cart button on SFL items
 * Restores product properties from cprops when adding to cart
 */
class MoveToCartButton extends HTMLElement {
  constructor() {
    super();
    try {
      this.button = this.querySelector('button, .move-to-cart-btn');
      this.manager = window.saveForLaterManager;
      if (this.button) {
        this.button.addEventListener('click', this.handleClick.bind(this));
      }
    } catch (e) {
      sflRollbar('MoveToCartButton.constructor', e);
    }
  }

  connectedCallback() {
    if (!window.saveForLaterManager) {
      window.saveForLaterManager = new SaveForLaterManager();
      this.manager = window.saveForLaterManager;
    }
  }

  /**
   * Get SFL item data from data attributes
   * @returns {Object} SFL item object
   */
  getSFLItemData() {
    const button = this.button;
    const item = {
      du: button.dataset.productUrl,
      empi: button.dataset.productId,
      epi: button.dataset.variantId,
      qty: button.dataset.quantity || 1,
      cprops: {}
    };

    // Try to get cprops from data attribute
    const cpropsData = button.dataset.cprops || this.dataset.cprops;
    if (cpropsData) {
      try {
        item.cprops = JSON.parse(cpropsData);
      } catch (e) {
        sflRollbar('MoveToCartButton.getSFLItemData.parse', e);
        console.warn('[SFL] Could not parse cprops data:', e);
      }
    }

    return item;
  }

  /**
   * Handle move to cart button click
   */
  async handleClick(event) {
    event.preventDefault();
    
    const button = this.button;
    const sflItem = this.getSFLItemData();

    if (!sflItem.du || !sflItem.empi || !sflItem.epi) {
      console.error('[SFL] Missing required SFL item data');
      return;
    }

    // Show loading state
    button.classList.add('loading');
    button.setAttribute('disabled', 'disabled');

    this.manager.moveToCart(
      sflItem,
      (response) => {
        // Show success message
        if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
          window.notificationEle.updateNotification(
            'Added to Cart',
            'Item has been moved to your cart',
            { type: 'success', timeout: 3000 }
          );
        }

        button.classList.remove('loading');
        button.removeAttribute('disabled');
      },
      (error) => {
        if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
          window.notificationEle.updateNotification(
            'Error',
            'Failed to move item to cart. Please try again.',
            { type: 'error', timeout: 5000 }
          );
        }

        button.classList.remove('loading');
        button.removeAttribute('disabled');
      }
    );
  }
}

wrapSFLPrototype(MoveToCartButton, 'MoveToCartButton');

// Initialize Save for Later Manager as a global instance
try {
  window.saveForLaterManager = new SaveForLaterManager();
} catch (e) {
  sflRollbar('bootstrap.SaveForLaterManager', e);
}

if (!customElements.get('save-for-later-button')) {
  try {
    customElements.define('save-for-later-button', SaveForLaterButton);
  } catch (e) {
    sflRollbar('bootstrap.defineSaveForLaterButton', e);
  }
}

if (!customElements.get('move-to-cart-button')) {
  try {
    customElements.define('move-to-cart-button', MoveToCartButton);
  } catch (e) {
    sflRollbar('bootstrap.defineMoveToCartButton', e);
  }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { SaveForLaterManager, SaveForLaterButton, MoveToCartButton };
}
